/**
 * Admin worker-jobs page (M9 Worker simplification).
 *
 * Three thin panels: cron health, mail failures, stuck audit packs.
 * Replaces the prior dashboard-driven dead-letter list + generic re-queue
 * button — three named failure modes, three retry paths, three shapes.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { JSX } from 'react';
import type { CronHealthEntry, MailFailureEntry, StuckAuditPackEntry } from '@isms/shared';
import { ApiError, type ApiClient } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';

export interface WorkerJobsPageProps {
  client: ApiClient;
  signInPath?: string;
}

interface State {
  cron: CronHealthEntry[];
  mailFailures: MailFailureEntry[];
  stuckAuditPacks: StuckAuditPackEntry[];
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; error: ApiError | string }
  | { kind: 'ready'; data: State };

type CronJobName = 'daily_reminder' | 'weekly_digest';
type CronRunMessage = { kind: 'success' | 'info' | 'error'; text: string };

export function WorkerJobsPage({
  client,
  signInPath = '/admin/sign-in',
}: WorkerJobsPageProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [retrying, setRetrying] = useState<Record<string, boolean>>({});
  const [deleting, setDeleting] = useState<Record<string, boolean>>({});
  const [retryError, setRetryError] = useState<ApiError | string | null>(null);
  const [cronRunning, setCronRunning] = useState<Record<string, boolean>>({});
  const [cronMessages, setCronMessages] = useState<Record<string, CronRunMessage>>({});

  const load = useCallback(async (): Promise<void> => {
    setState({ kind: 'loading' });
    try {
      const [health, mailFailures, stuckAuditPacks] = await Promise.all([
        client.getAdminCronHealth(),
        client.getAdminMailFailures(),
        client.getAdminStuckAuditPacks(),
      ]);
      setState({
        kind: 'ready',
        data: {
          cron: health.jobs,
          mailFailures: mailFailures.items,
          stuckAuditPacks: stuckAuditPacks.items,
        },
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        window.location.assign(signInPath);
        return;
      }
      setState({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : err instanceof Error
              ? err.message
              : 'Failed to load worker jobs.',
      });
    }
  }, [client, signInPath]);

  useEffect(() => {
    void load();
  }, [load]);

  const onRetryMail = useCallback(
    async (failureId: string) => {
      setRetrying((s) => ({ ...s, [failureId]: true }));
      setRetryError(null);
      try {
        await client.retryAdminMailFailure(failureId);
        await load();
      } catch (err) {
        setRetryError(
          err instanceof ApiError ? err : err instanceof Error ? err.message : 'Retry failed.',
        );
      } finally {
        setRetrying((s) => ({ ...s, [failureId]: false }));
      }
    },
    [client, load],
  );

  const onDeleteMail = useCallback(
    async (failureId: string) => {
      if (!window.confirm('Delete this mail failure? This cannot be undone.')) return;
      setDeleting((s) => ({ ...s, [failureId]: true }));
      setRetryError(null);
      try {
        await client.deleteAdminMailFailure(failureId);
        await load();
      } catch (err) {
        setRetryError(
          err instanceof ApiError ? err : err instanceof Error ? err.message : 'Delete failed.',
        );
      } finally {
        setDeleting((s) => ({ ...s, [failureId]: false }));
      }
    },
    [client, load],
  );

  const onRunCronJob = useCallback(
    async (jobName: CronJobName) => {
      setCronRunning((s) => ({ ...s, [jobName]: true }));
      setCronMessages((s) => {
        const { [jobName]: _, ...rest } = s;
        return rest;
      });
      try {
        const result = await client.runAdminCronJob(jobName);
        let message: CronRunMessage;
        if (result.outcome === 'succeeded') {
          message = {
            kind: 'success',
            text: result.skipped_empty
              ? `Run completed (no work this bucket).`
              : `Run completed (id ${result.run_id ?? '?'}).`,
          };
        } else if (result.outcome === 'idempotent_skip') {
          message = {
            kind: 'info',
            text: 'Already ran in this bucket — idempotent skip.',
          };
        } else {
          message = {
            kind: 'error',
            text: `Handler failed (id ${result.run_id ?? '?'}). See cron health for details.`,
          };
        }
        setCronMessages((s) => ({ ...s, [jobName]: message }));
        await load();
      } catch (err) {
        setCronMessages((s) => ({
          ...s,
          [jobName]: {
            kind: 'error',
            text: err instanceof Error ? err.message : 'Run failed.',
          },
        }));
      } finally {
        setCronRunning((s) => ({ ...s, [jobName]: false }));
      }
    },
    [client, load],
  );

  const totals = useMemo(() => {
    if (state.kind !== 'ready') return { mail: 0, audit: 0, staleCron: 0 };
    return {
      mail: state.data.mailFailures.filter((m) => m.retried_outcome == null).length,
      audit: state.data.stuckAuditPacks.length,
      staleCron: state.data.cron.filter((c) => c.stale).length,
    };
  }, [state]);

  if (state.kind === 'loading') {
    return (
      <main className="admin-worker-jobs" aria-busy="true">
        <p>Loading worker jobs…</p>
      </main>
    );
  }
  if (state.kind === 'error') {
    return (
      <main className="admin-worker-jobs">
        <ErrorBanner error={state.error} />
        <button type="button" className="admin-worker-jobs-refresh" onClick={() => void load()}>
          Retry
        </button>
      </main>
    );
  }
  const { cron, mailFailures, stuckAuditPacks } = state.data;
  return (
    <main className="admin-worker-jobs" aria-labelledby="admin-worker-jobs-heading">
      <header className="admin-worker-jobs-header">
        <p>
          <Link to="/admin">← Back to dashboard</Link>
        </p>
        <h1 id="admin-worker-jobs-heading">Worker jobs</h1>
        <p className="admin-worker-jobs-intro">
          Cron health, mail-send failures, and stuck audit-pack runs. Each panel surfaces a named
          failure mode with its own retry path.
        </p>
      </header>

      {retryError ? <ErrorBanner error={retryError} /> : null}

      <section className="admin-worker-jobs-section" aria-labelledby="cron-health">
        <div className="admin-worker-jobs-section-header">
          <h2 id="cron-health">
            Cron health
            <CountBadge count={totals.staleCron} alert={totals.staleCron > 0} label="stale" />
          </h2>
          <button type="button" className="admin-worker-jobs-refresh" onClick={() => void load()}>
            Refresh
          </button>
        </div>
        {cron.length === 0 ? (
          <p>No scheduled jobs configured.</p>
        ) : (
          <ul className="admin-worker-jobs-health-list">
            {cron.map((j) => {
              const running = cronRunning[j.job_name] === true;
              const message = cronMessages[j.job_name];
              return (
                <li
                  key={j.job_name}
                  className={`admin-worker-jobs-health-row${j.stale ? ' is-alert' : ''}`}
                >
                  <span className="admin-worker-jobs-health-name">{j.job_name}</span>
                  <span className="admin-worker-jobs-health-last-success">
                    {j.last_succeeded_at == null ? (
                      'Never run'
                    ) : (
                      <>
                        Last success{' '}
                        <time dateTime={toIso(j.last_succeeded_at)}>
                          {formatDateTime(j.last_succeeded_at)}
                        </time>
                      </>
                    )}
                  </span>
                  <span className="admin-worker-jobs-health-dead-letter">
                    <button
                      type="button"
                      className="admin-worker-jobs-requeue-button"
                      onClick={() => void onRunCronJob(j.job_name)}
                      disabled={running}
                      aria-label={`Run ${j.job_name} now`}
                    >
                      {running ? 'Running…' : 'Run now'}
                    </button>
                  </span>
                  {message ? (
                    <span
                      className="admin-worker-jobs-health-last-error"
                      role={message.kind === 'error' ? 'alert' : 'status'}
                      style={
                        message.kind === 'success'
                          ? { color: 'var(--isms-color-success, #207940)' }
                          : message.kind === 'info'
                            ? { color: 'var(--isms-color-text-muted)' }
                            : undefined
                      }
                    >
                      {message.text}
                    </span>
                  ) : null}
                  {j.last_failed_message ? (
                    <span className="admin-worker-jobs-health-last-error">
                      {j.last_failed_message}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="admin-worker-jobs-section" aria-labelledby="mail-failures">
        <div className="admin-worker-jobs-section-header">
          <h2 id="mail-failures">
            Mail failures
            <CountBadge count={totals.mail} alert={totals.mail > 0} label="unretried" />
          </h2>
        </div>
        {mailFailures.length === 0 ? (
          <p>No mail failures recorded.</p>
        ) : (
          <ul className="admin-worker-jobs-dead-letter-list">
            {mailFailures.map((f) => {
              const failureKey = String(f.failure_id);
              const retried = f.retried_outcome != null;
              const succeeded = f.retried_outcome === 'succeeded';
              return (
                <li key={failureKey} className="admin-worker-jobs-dead-letter-row">
                  <div className="admin-worker-jobs-dl-head">
                    <span className="admin-worker-jobs-dl-name">{f.template_name}</span>
                    <span className="admin-worker-jobs-dl-id">#{failureKey}</span>
                  </div>
                  <dl className="admin-worker-jobs-dl-meta">
                    <dt>To</dt>
                    <dd>{f.to_email}</dd>
                    <dt>Subject</dt>
                    <dd>{f.rendered_subject}</dd>
                    <dt>Failed at</dt>
                    <dd>
                      <time dateTime={toIso(f.failed_at)}>{formatDateTime(f.failed_at)}</time>
                    </dd>
                    <dt>Status</dt>
                    <dd>
                      {succeeded ? 'Retry succeeded' : retried ? 'Retry failed' : 'Unretried'}
                    </dd>
                  </dl>
                  <p className="admin-worker-jobs-dl-error">
                    {f.retried_error_message ?? f.error_message}
                  </p>
                  <div className="admin-worker-jobs-dl-actions">
                    {!succeeded ? (
                      <button
                        type="button"
                        className="admin-worker-jobs-requeue-button"
                        onClick={() => void onRetryMail(failureKey)}
                        disabled={retrying[failureKey] || deleting[failureKey]}
                      >
                        {retrying[failureKey] ? 'Retrying…' : 'Retry send'}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="admin-worker-jobs-delete-button"
                      onClick={() => void onDeleteMail(failureKey)}
                      disabled={retrying[failureKey] || deleting[failureKey]}
                    >
                      {deleting[failureKey] ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="admin-worker-jobs-section" aria-labelledby="audit-packs">
        <div className="admin-worker-jobs-section-header">
          <h2 id="audit-packs">
            Stuck audit packs
            <CountBadge count={totals.audit} alert={totals.audit > 0} label="stuck" />
          </h2>
        </div>
        {stuckAuditPacks.length === 0 ? (
          <p>No stuck audit pack runs.</p>
        ) : (
          <ul className="admin-worker-jobs-dead-letter-list">
            {stuckAuditPacks.map((p) => {
              const id = String(p.audit_pack_run_id);
              return (
                <li key={id} className="admin-worker-jobs-dead-letter-row">
                  <div className="admin-worker-jobs-dl-head">
                    <span className="admin-worker-jobs-dl-name">Audit pack</span>
                    <span className="admin-worker-jobs-dl-id">#{id}</span>
                  </div>
                  <dl className="admin-worker-jobs-dl-meta">
                    <dt>Status</dt>
                    <dd>{p.status}</dd>
                    <dt>Requested at</dt>
                    <dd>
                      <time dateTime={toIso(p.requested_at)}>{formatDateTime(p.requested_at)}</time>
                    </dd>
                    {p.started_at ? (
                      <>
                        <dt>Started at</dt>
                        <dd>
                          <time dateTime={toIso(p.started_at)}>{formatDateTime(p.started_at)}</time>
                        </dd>
                      </>
                    ) : null}
                  </dl>
                  {p.error_message ? (
                    <p className="admin-worker-jobs-dl-error">{p.error_message}</p>
                  ) : (
                    <p className="admin-worker-jobs-dl-error-empty">
                      No error message — likely abandoned mid-run.
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
}

function CountBadge({
  count,
  alert,
  label,
}: {
  count: number;
  alert: boolean;
  label: string;
}): JSX.Element | null {
  if (count <= 0) return null;
  return (
    <span
      className={`admin-worker-jobs-count${alert ? ' is-alert' : ''}`}
      aria-label={`${count} ${label}`}
    >
      {count}
    </span>
  );
}

function toIso(v: string | Date): string {
  return typeof v === 'string' ? v : v.toISOString();
}

function formatDateTime(v: string | Date): string {
  const d = typeof v === 'string' ? new Date(v) : v;
  if (Number.isNaN(d.getTime())) return String(v);
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
